import { errorLogger } from "@/Common/helpers/logger";
import {
	ISubmitPayrollToHRResponse,
	IUpdateCommissionCycleInReviewStatus,
	IUpdateCommissionCycleStatus,
	IApproveAllWashoutsArgs,
	TCreateAdjustmentsParams,
	TUpdateAdjustmentParams,
	TDeleteAdjustmentParams,
	ISubmitPayrollToHRArgs,
} from "@/Washouts/types/PayrollClosing/mutations";
import { apiInstance, gql } from "@/services/httpAdapter";
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { IAdjustment } from "@/Washouts/types/PayrollClosing/queries";
import { ICommissionCycle, IStatus } from "@/Common/api/types/CommissionCycle";

export async function updateCommissionCycleStatus({
	locationId,
	commissionCycleId,
	payPeriodId,
	status,
	positionStatus,
}: {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	status: string;
	positionStatus: { position: string; status: "REVIEWING" }[];
}) {
	const query = gql`
		mutation updateCommissionCycleStatus(
			$locationId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$status: String!
			$positionStatus: [PositionStatusInput]
		) {
			updateCommissionCycleStatus(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				status: $status
				positionStatus: $positionStatus
			) {
				locationId
				commissionCycleId
				status
				updatedCommissionCycle {
					_id
					title
					status
					startDate
					endDate
					dataUpdatedAt
					payPeriods {
						_id
						title
						intervalCode
						startDate
						endDate
						inReview
						positionStatus {
							position
							status
						}
						payPeriodProviders {
							status
							providerPayPeriodId
						}
						nextCCId
						previousCCId
						status
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		status,
		positionStatus,
	};

	try {
		const response: GraphqlQueryResponse<"updateCommissionCycleStatus", IUpdateCommissionCycleStatus> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.updateCommissionCycleStatus;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function updateCommissionCycleInReviewStatus(
	locationId: string,
	commissionCycleId: string,
	payPeriodId: string,
	status: boolean
) {
	const query = gql`
		mutation updateCommissionCycleInReviewStatus(
			$locationId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$input: Boolean
		) {
			updateCommissionCycleInReviewStatus(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				input: $input
			) {
				commissionCycleId
				payPeriodId
				locationId
				inReview
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		input: status,
	};

	try {
		const response: GraphqlQueryResponse<"updateCommissionCycleInReviewStatus", IUpdateCommissionCycleInReviewStatus> =
			await apiInstance.post("/graphql", {
				query,
				variables,
			});
		return response.data.data?.updateCommissionCycleInReviewStatus;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function submitPayrollToHR({
	locationId,
	commissionCycleId,
	payPeriodId,
	companyIdProviderPayPeriodIdPairs,
	submitterId,
	payrollData,
}: ISubmitPayrollToHRArgs) {
	const query = gql`
		mutation submitPayrollToHR(
			$locationId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$companyIdProviderPayPeriodIdPairs: [CompanyIdProviderPayPeriodIdPairInput]!
			$submitterId: String!
			$input: [PayrollCommissionInput]
		) {
			submitPayrollToHR(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				companyIdProviderPayPeriodIdPairs: $companyIdProviderPayPeriodIdPairs
				submitterId: $submitterId
				input: $input
			) {
				locationId
				commissionCycleId
				payPeriodId
				updated {
					_id
					columns {
						label
						columnType
					}
					data {
						row {
							label
							value
						}
					}
					status
					position
					statusDate
					companyId
					displayId
					providerPayPeriodId
					submitter {
						_id
						fullName
					}
					dateRange
				}
			}
		}
	`;
	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		companyIdProviderPayPeriodIdPairs,
		submitterId,
		input: payrollData,
	};

	try {
		const response: GraphqlQueryResponse<"submitPayrollToHR", ISubmitPayrollToHRResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.submitPayrollToHR;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function deleteSubmittedPayroll(locationId: string, payrollSubmissionIds: string[]) {
	const query = gql`
		mutation deleteSubmittedPayroll($locationId: String!, $submissionIds: [String]) {
			deleteSubmittedPayroll(locationId: $locationId, submissionIds: $submissionIds)
		}
	`;

	const variables = {
		locationId,
		submissionIds: payrollSubmissionIds,
	};

	try {
		const response: GraphqlQueryResponse<"deleteSubmittedPayroll", boolean> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.deleteSubmittedPayroll;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function createAdjustments({ locationId, payPeriodId, input }: TCreateAdjustmentsParams) {
	const query = gql`
		mutation createAdjustments($locationId: String, $payPeriodId: String, $input: CreateAdjustmentsInput) {
			createAdjustments(locationId: $locationId, payPeriodId: $payPeriodId, input: $input) {
				locationId
				payPeriodId
				adjustments {
					_id
					amount
					recipient {
						_id
						fullName
					}
					checkNumber
					adjustmentType
					createdBy {
						_id
						fullName
					}
					createdOn
					description
					lastModifiedBy
					lastModifiedOn
				}
			}
		}
	`;

	const variables = {
		locationId,
		payPeriodId,
		input,
	};

	try {
		const response: GraphqlQueryResponse<"createAdjustments", IAdjustment> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.createAdjustments;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function deleteAdjustment({ ids, locationId, payPeriodId, userName }: TDeleteAdjustmentParams) {
	const query = gql`
		mutation deleteAdjustment($id: [String], $locationId: String, $payPeriodId: String, $userName: String!) {
			deleteAdjustment(id: $id, locationId: $locationId, payPeriodId: $payPeriodId, userName: $userName) {
				locationId
				payPeriodId
				adjustments {
					_id
					amount
					recipient {
						_id
						fullName
					}
					checkNumber
					adjustmentType
					createdBy {
						_id
						fullName
					}
					createdOn
					description
					lastModifiedBy
					lastModifiedOn
				}
			}
		}
	`;

	const variables = {
		id: ids,
		locationId,
		payPeriodId,
		userName,
	};

	try {
		const response: GraphqlQueryResponse<"deleteAdjustment", IAdjustment> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.deleteAdjustment;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function updateAdjustment({ locationId, payPeriodId, input, id }: TUpdateAdjustmentParams) {
	const query = gql`
		mutation updateAdjustment($locationId: String!, $payPeriodId: String, $input: UpdateAdjustmentInput, $id: String) {
			updateAdjustment(locationId: $locationId, payPeriodId: $payPeriodId, input: $input, id: $id) {
				locationId
				payPeriodId
				adjustments {
					_id
					amount
					recipient {
						_id
						fullName
					}
					checkNumber
					adjustmentType
					createdBy {
						_id
						fullName
					}
					createdOn
					description
					lastModifiedBy
					lastModifiedOn
				}
			}
		}
	`;

	const variables = {
		locationId,
		payPeriodId,
		input,
		id,
	};

	try {
		const response: GraphqlQueryResponse<"updateAdjustment", IAdjustment> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.updateAdjustment;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function approveAllWashouts(args: IApproveAllWashoutsArgs) {
	const { userId, locationId, commissionCycleId, payPeriodId, positions } = args;
	const query = gql`
		mutation approveAllWashouts(
			$userId: String!
			$locationId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$positions: [String]
		) {
			approveAllWashouts(
				userId: $userId
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				positions: $positions
			) {
				commissionCycleId
				locationId
				payPeriodId
				statusSetBy
				statusSetOn
				positionStatus {
					position
					status
				}
			}
		}
	`;

	const variables = {
		userId,
		locationId,
		commissionCycleId,
		payPeriodId,
		positions,
	};

	try {
		const response: GraphqlQueryResponse<"approveAllWashouts", IStatus[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.approveAllWashouts;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function updateCommissionCycleToFinalizingProcess(locationId: string, commissionCycleId: string, userId: string) {
	const query = gql`
		mutation updateCommissionCycleToFinalizingProcess($locationId: String!, $commissionCycleId: String!, $userId: String!) {
			updateCommissionCycleToFinalizingProcess(locationId: $locationId, commissionCycleId: $commissionCycleId, userId: $userId) {
				_id
				locationId
				status
				inFinalizingProcess
				finalizingByUserId
				title
				startDate
				endDate
				dataUpdatedAt
				payPeriods {
					_id
					title
					intervalCode
					startDate
					endDate
					inReview
					positionStatus {
						position
						status
					}
					payPeriodProviders {
						status
						providerPayPeriodId
					}
					nextCCId
					previousCCId
					status
				}
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		userId,
	};

	try {
		const response: GraphqlQueryResponse<"updateCommissionCycleToFinalizingProcess", ICommissionCycle> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.updateCommissionCycleToFinalizingProcess;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}
